import React, { useEffect, useState, Fragment } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getInsContacts,
  getUpdateErrors,
  addInsContact,
  getDefaultInsurersList,
  getDefaultInsurersListSelector,
  toggleInsContactsModal,
  getInsContactsStatus,
  addBrokerageInsurer,
  updateIsActiveStatus,
  updateAllIsActiveStatus,
} from "../store/brokerage";
import Select from "react-select";
import axios from "axios";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  ArcElement,
  Legend,
} from "chart.js";
import { Bar, Pie, Line } from "react-chartjs-2";
import styled from "styled-components";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import moment from "moment";

// Styled Components
const Container = styled.div`
  padding: 20px;
  background-color: #f5f5f5;
`;

const DateRangePicker = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const DateInput = styled.input`
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  margin-left: 10px;
`;

const InsurerAdminData = (props) => {
  const dispatch = useDispatch();
  const insContacts = useSelector(getDefaultInsurersListSelector);
  const [insurerOptions, setInsurerOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [insurerData, setInsurerData] = useState();
  const [selectedInsurerName, setSelectedInsurerName] = useState();
  const [formattedData, setFormattedData] = useState();
  const [selectedInsurerBinderBreakdown, setSelectedInsurerBinderBreakdown] =
    useState([]);

  // Introduce startDate and endDate states
  const [startDate, setStartDate] = useState(
    moment().subtract(1, "months").format("YYYY-MM-DD")
  );
  const [endDate, setEndDate] = useState(moment().format("YYYY-MM-DD"));

  const [tableData, setTableData] = useState([]);

  const downloadPDF = async () => {
    const input = document.getElementById("pdfContent");
    const canvas = await html2canvas(input);
    const imgData = canvas.toDataURL("image/png");
    let pdf = new jsPDF();
    let imgHeight = (canvas.height * 208) / canvas.width;
    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, "PNG", 0, position, 208, imgHeight);
    heightLeft -= 290;

    while (heightLeft >= 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, "PNG", 0, position, 208, imgHeight);
      heightLeft -= 290;
    }
    pdf.save(selectedInsurerName + "_chart.pdf");
  };

  const filterDataByDateRange = (data, startDate, endDate) => {
    const start = moment(startDate, "YYYY-MM-DD");
    const end = moment(endDate, "YYYY-MM-DD");
    console.log("show data type", data);
    return data.filter((item) => {
      const policyBindDate = moment(item.policyBindDate, "YYYY-MM-DD");
      return policyBindDate.isBetween(start, end, "days", "[]"); // inclusive of start and end dates
    });
  };

  const convertToCSV = (objArray) => {
    const array =
      typeof objArray !== "object" ? JSON.parse(objArray) : objArray;

    let str = "";
    // Extract headers
    const headers = Object.keys(array[0]);
    str += headers.join(",") + "\r\n";

    // Extract rows
    for (let i = 0; i < array.length; i++) {
      let line = "";
      for (let index in headers) {
        if (line !== "") line += ",";
        // Handle undefined values
        const value = array[i][headers[index]];
        line += value === undefined ? "" : `"${value}"`; // Enclose each value in quotes to handle commas within fields
      }
      str += line + "\r\n";
    }
    return str;
  };

  const downloadCSV = () => {
    if (selectedInsurerBinderBreakdown.length) {
      const filteredInsurerData = filterDataByDateRange(
        selectedInsurerBinderBreakdown,
        startDate,
        endDate
      );
      const csvData = convertToCSV(filteredInsurerData);
      const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", "insurerBinderBreakdown.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      alert("please select insurer");
    }
  };

  useEffect(() => {
    if (insContacts.length === 0) {
      dispatch(getDefaultInsurersList(props.brokerageCode));
    } else {
      const tempInsurer = insContacts.map((insurer) => {
        return {
          value: insurer.code,
          label: insurer.name,
          insurerId: insurer._id,
        };
      });
      setInsurerOptions(tempInsurer);
    }
  }, [insContacts]);

  const getAddInsurerStatus = (insurerCode, insurerName) => {
    axios
      .post(
        process.env.REACT_APP_BASICSERVICE + "/api/prospects/getInsurerStats",
        { insurercode: insurerCode, insurerName: insurerName },
        {
          headers: { "x-auth-token": localStorage.getItem("jwt") },
        }
      )
      .then((response) => {
        setInsurerData(response.data.stats);
        const tempBinderBreakdown = response.data.insurerBindersBreakdown.map(
          (data) => {
            // Convert and format policyBindDate
            data.policyBindDate = moment
              .utc(data.policyBindDate)
              .format("YYYY-MM-DD HH:mm:ss");

            // Convert and format policyEffectiveDate
            data.policyEffectiveDate = moment
              .utc(data.policyEffectiveDate)
              .format("YYYY-MM-DD HH:mm:ss");

            // Convert and format policyExpiryDate

            data.policyExpiryDate = moment
              .utc(data.policyExpiryDate)
              .format("YYYY-MM-DD HH:mm:ss");

            return data; // Make sure to return the modified data object
          }
        );

        setSelectedInsurerBinderBreakdown(tempBinderBreakdown);
      })
      .catch((error) => {
        console.error("Error fetching insurer stats:", error);
      });
  };

  const formatChartData = () => {
    if (!insurerData) return;
    // Filter insurerData based on startDate and endDate
    const filteredData = insurerData.filter((item) => {
      const date = new Date(item._id.date);
      return (
        (!startDate || date >= new Date(startDate)) &&
        (!endDate || date <= new Date(endDate))
      );
    });

    const aggregatedData = filteredData.reduce((acc, item) => {
      if (!acc[item._id.date]) {
        acc[item._id.date] = {
          date: item._id.date,
          quote: 0,
          refer: 0,
          decline: 0,
        };
      }
      if (item._id.status === "Quote") acc[item._id.date].quote += item.count;
      if (item._id.status === "Refer") acc[item._id.date].refer += item.count;
      if (item._id.status === "Decline")
        acc[item._id.date].decline += item.count;

      return acc;
    }, {});

    const tableEntries = Object.values(aggregatedData);
    setTableData(tableEntries);

    // const labels = filteredData?.map((item) => item._id.date);

    const labels = tableEntries.map((item) => item.date);
    const quoteData = tableEntries.map((item) => item.quote);
    const referData = tableEntries.map((item) => item.refer);
    const declineData = tableEntries.map((item) => item.decline);

    return {
      labels,
      datasets: [
        {
          label: "Quote",
          data: quoteData,
          backgroundColor: "rgba(75, 192, 192, 0.6)",
          borderColor: "rgba(75, 192, 192, 1)",
          borderWidth: 1,
        },
        {
          label: "Refer",
          data: referData,
          backgroundColor: "rgba(255, 206, 86, 0.6)",
          borderColor: "rgba(255, 206, 86, 1)",
          borderWidth: 1,
        },
        {
          label: "Decline",
          data: declineData,
          backgroundColor: "rgba(255, 99, 132, 0.6)",
          borderColor: "rgba(255, 99, 132, 1)",
          borderWidth: 1,
        },
      ],
    };
  };

  useEffect(() => {
    if (insurerData != undefined) {
      setFormattedData(formatChartData());
    }
  }, [startDate, endDate, insurerData]);

  return (
    <Container>
      <div style={{ padding: "10px" }}>
        <label>Start Date:</label>
        <DateInput
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
        />
        <label>End Date:</label>
        <DateInput
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
        />
      </div>
      <Select
        options={insurerOptions}
        onChange={(option) => {
          setSelectedOption(option);
          getAddInsurerStatus(option.value, option.label);
          setSelectedInsurerName(option.label);
        }}
      />
      <button style={{ marginTop: "12px" }} onClick={downloadPDF}>
        Download as PDF
      </button>

      <button style={{ marginTop: "12px" }} onClick={downloadCSV}>
        Download Binder Data as CSV
      </button>

      {/* Here you can render the insurerData or use it in any chart component to display the stats */}
      <div style={{ padding: "10px" }} id="pdfContent">
        <h2 style={{ textAlign: "center" }}>{selectedInsurerName}</h2>
        <h4 style={{ textAlign: "center" }}>
          {" "}
          {startDate} ~ {endDate}{" "}
        </h4>
        {insurerData && formattedData && (
          <Bar
            data={formattedData}
            options={{
              responsive: true,
              scales: {
                x: {
                  beginAtZero: true,
                  stacked: true,
                },
                y: {
                  beginAtZero: true,
                  stacked: true,
                },
              },
            }}
          />
        )}
        <table
          style={{
            marginTop: "20px",
            width: "100%",
            borderCollapse: "collapse",
          }}
        >
          <thead>
            <tr>
              <th style={{ border: "1px solid black", padding: "8px" }}>
                Date
              </th>
              <th style={{ border: "1px solid black", padding: "8px" }}>
                Quote
              </th>
              <th style={{ border: "1px solid black", padding: "8px" }}>
                Refer
              </th>
              <th style={{ border: "1px solid black", padding: "8px" }}>
                Decline
              </th>
            </tr>
          </thead>
          <tbody>
            {tableData.map((entry) => (
              <tr key={entry.date}>
                <td style={{ border: "1px solid black", padding: "8px" }}>
                  {entry.date}
                </td>
                <td style={{ border: "1px solid black", padding: "8px" }}>
                  {entry.quote}
                </td>
                <td style={{ border: "1px solid black", padding: "8px" }}>
                  {entry.refer}
                </td>
                <td style={{ border: "1px solid black", padding: "8px" }}>
                  {entry.decline}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Container>
  );
};

export default InsurerAdminData;
